// Section + field configuration for the Peace of Mind Legacy vault
import {
  Heart, User, Stethoscope, Landmark, Home, Globe,
  Flower2, BookHeart, CalendarClock,
} from "lucide-react";

export const SECTIONS = [
  {
    id: "welcome",
    title: "Welcome",
    icon: Heart,
    tagline: "Begin with intention and love.",
    intro:
      "This vault is a gift to those you love — a calm, organized place to record the details of your life, estate, and final wishes. Take your time. There is no rush. Every entry brings your family greater peace of mind.",
    fields: [
      { key: "prepared_by", label: "This vault is prepared by", type: "text", placeholder: "Your full name" },
      { key: "date_started", label: "Date started", type: "date" },
      { key: "personal_message", label: "A note to my family", type: "textarea", placeholder: "Why I created this legacy record…" },
    ],
  },
  {
    id: "personal",
    title: "Personal Information",
    icon: User,
    tagline: "The essentials, gathered in one place.",
    intro: "Your core personal details and the people closest to you.",
    fields: [
      { key: "full_name", label: "Full legal name", type: "text" },
      { key: "date_of_birth", label: "Date of birth", type: "date" },
      { key: "place_of_birth", label: "Place of birth", type: "text" },
      { key: "ssn", label: "Social Security / National ID", type: "text" },
      { key: "home_address", label: "Home address", type: "textarea" },
      { key: "phone", label: "Phone", type: "tel" },
      { key: "email", label: "Email", type: "email" },
      { key: "marital_status", label: "Marital status", type: "select", options: ["Single", "Married", "Partnered", "Divorced", "Widowed"] },
      { key: "spouse_partner", label: "Spouse / partner name", type: "text" },
      { key: "children", label: "Children / dependents", type: "textarea", placeholder: "Names and dates of birth" },
      { key: "emergency_contacts", label: "Emergency contacts", type: "textarea", placeholder: "Name, relationship, phone" },
    ],
  },
  {
    id: "medical",
    title: "Medical Information",
    icon: Stethoscope,
    tagline: "Care preferences and vital health details.",
    intro: "Health details and care wishes to guide loved ones and caregivers.",
    fields: [
      { key: "primary_physician", label: "Primary physician", type: "text" },
      { key: "physician_phone", label: "Physician phone", type: "tel" },
      { key: "blood_type", label: "Blood type", type: "text" },
      { key: "allergies", label: "Allergies", type: "textarea" },
      { key: "medications", label: "Current medications & dosages", type: "textarea" },
      { key: "conditions", label: "Medical conditions", type: "textarea" },
      { key: "pharmacy", label: "Preferred pharmacy", type: "text" },
      { key: "insurance", label: "Health insurance provider & policy #", type: "textarea" },
      { key: "healthcare_proxy", label: "Healthcare proxy / agent", type: "text" },
      { key: "advance_directive", label: "Advance directive / DNR wishes", type: "textarea" },
    ],
  },
  {
    id: "financial",
    title: "Financial & Legal",
    icon: Landmark,
    tagline: "Accounts, documents, and trusted advisors.",
    intro: "A clear record of your financial life and legal documents.",
    fields: [
      { key: "bank_accounts", label: "Bank accounts", type: "textarea", placeholder: "Bank, account type, last 4 digits" },
      { key: "investments", label: "Investment & retirement accounts", type: "textarea" },
      { key: "life_insurance", label: "Life insurance policies", type: "textarea" },
      { key: "debts", label: "Debts & loans", type: "textarea" },
      { key: "will_location", label: "Location of will / trust", type: "text" },
      { key: "power_of_attorney", label: "Power of attorney", type: "text" },
      { key: "attorney", label: "Attorney (name & contact)", type: "text" },
      { key: "accountant", label: "Accountant / financial advisor", type: "text" },
      { key: "safe_deposit", label: "Safe deposit box location & key", type: "text" },
    ],
  },
  {
    id: "property",
    title: "Home & Property",
    icon: Home,
    tagline: "Where things are and how they're held.",
    intro: "Real estate, vehicles, and valued possessions.",
    fields: [
      { key: "real_estate", label: "Real estate owned", type: "textarea" },
      { key: "mortgage", label: "Mortgage / lender details", type: "textarea" },
      { key: "vehicles", label: "Vehicles (make, model, title location)", type: "textarea" },
      { key: "home_insurance", label: "Home / property insurance", type: "textarea" },
      { key: "storage", label: "Storage units", type: "text" },
      { key: "valuables", label: "Valuables & heirlooms", type: "textarea" },
      { key: "keys_location", label: "Location of keys & access codes", type: "text" },
    ],
  },
  {
    id: "digital",
    title: "Digital Estate",
    icon: Globe,
    tagline: "Your online life, made accessible.",
    intro: "Digital accounts and assets your family may need to manage.",
    fields: [
      { key: "email_accounts", label: "Email accounts", type: "textarea" },
      { key: "social_media", label: "Social media accounts", type: "textarea" },
      { key: "password_manager", label: "Password manager & master access plan", type: "textarea" },
      { key: "subscriptions", label: "Subscriptions & recurring services", type: "textarea" },
      { key: "cloud_storage", label: "Cloud storage & photos", type: "textarea" },
      { key: "cryptocurrency", label: "Cryptocurrency / digital wallets", type: "textarea" },
      { key: "devices", label: "Devices & unlock codes", type: "textarea" },
    ],
  },
  {
    id: "final_wishes",
    title: "Final Wishes",
    icon: Flower2,
    tagline: "A service that reflects who you are.",
    intro: "Your preferences for how you'd like to be honored and remembered.",
    fields: [
      { key: "disposition", label: "Burial or cremation preference", type: "select", options: ["Burial", "Cremation", "Green / natural burial", "Donation to science", "Undecided"] },
      { key: "resting_place", label: "Preferred resting place", type: "text" },
      { key: "service_type", label: "Type of service", type: "text", placeholder: "Traditional, celebration of life, private…" },
      { key: "service_wishes", label: "Service wishes (music, readings, tone)", type: "textarea", assist: true, aiMode: "final_wishes" },
      { key: "obituary_notes", label: "Obituary notes", type: "textarea", assist: true, aiMode: "final_wishes" },
      { key: "organ_donation", label: "Organ donation wishes", type: "text" },
      { key: "funeral_home", label: "Preferred funeral home / prearrangements", type: "textarea" },
    ],
  },
  {
    id: "legacy",
    title: "Legacy",
    icon: BookHeart,
    tagline: "The words and values you leave behind.",
    intro: "Letters, values, and memories to pass on to those you love.",
    fields: [
      { key: "letters_to_loved_ones", label: "Letters to loved ones", type: "textarea", assist: true, aiMode: "legacy" },
      { key: "ethical_will", label: "Values & life lessons (ethical will)", type: "textarea", assist: true, aiMode: "legacy" },
      { key: "personal_history", label: "Personal history & stories", type: "textarea", assist: true, aiMode: "legacy" },
      { key: "charitable_wishes", label: "Charitable wishes", type: "textarea" },
    ],
  },
  {
    id: "reviews",
    title: "Annual Reviews & Notes",
    icon: CalendarClock,
    tagline: "Keep it current, year after year.",
    intro: "Record when you last reviewed this vault and any additional notes.",
    fields: [
      { key: "last_reviewed", label: "Last reviewed", type: "date" },
      { key: "review_log", label: "Review log", type: "textarea", placeholder: "Date — what changed" },
      { key: "additional_notes", label: "Additional notes", type: "textarea" },
    ],
  },
];

export const BUNDLE = [
  { name: "Printable PDF Vault", desc: "The complete 9-section vault, beautifully typeset." },
  { name: "Fillable PDF", desc: "Type directly into every worksheet on your computer." },
  { name: "US Letter & A4", desc: "Both paper sizes included for worldwide printing." },
  { name: "Emergency Wallet Card", desc: "Key contacts and info to carry with you." },
  { name: "Medical Information Card", desc: "Allergies, medications, and proxy at a glance." },
  { name: "Family Emergency Checklist", desc: "What to do first, step by step." },
  { name: "Estate Document Checklist", desc: "Ensure nothing important is missed." },
  { name: "Digital Asset Inventory", desc: "Track every online account and password." },
  { name: "Funeral Planning Quick Guide", desc: "Gentle guidance for a meaningful service." },
];
